
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { isEmpty } from 'lodash';
import { NodeType } from '@amplify/amp-module';
import { useNodeUtils } from '@/composables/useNodeUtils';
import { useStoreInNodes } from '@/composables/useStoreInNodes';
import { GetPluginById } from '@/constants/actions';
import { NodeSizes } from '@/constants/builder';
import PluginFieldsPopup from '@/components/graph/nodes/plugin-node/PluginFieldsPopup';
import NodeWrapper from '@/components/graph/common/NodeWrapper';

export default {
  components: { PluginFieldsPopup, NodeWrapper },

  setup() {
    const nodeType = NodeType.plugin;
    const nodeActions = [{ click: true, key: 'bottom' }];
    const maxStorageSize = NodeSizes[nodeType].width - 3;

    const { store } = useStoreInNodes();
    const {
      ampData,
      uiData,
      nodeId,
      nodeRef,
      nodeSelf,
      canEditAmp,
      addStorageOption,
      bringNodeToFront,
      deleteStorage,
      emitNodeRenderDoneEvent,
    } = useNodeUtils({ nodeType });

    const rendered = ref(false);
    const showFieldsPopup = ref(false);
    const settings = ref([]);
    const pluginName = ref('');
    const loading = ref(false);

    const isDone = computed(() => isEmpty(ampData.value.settings || {}) || showSettings.value);
    const showSettings = computed(() => !!Object.values(ampData.value.settings || {}).find((value) => !!value));

    watch(rendered, async (newVal, oldVal) => {
      if (newVal && !oldVal) {
        await nextTick();
        emitNodeRenderDoneEvent();
      }
    });

    onMounted(async () => {
      await getPluginSettings(ampData.value.settings);
    });

    const onSettingsUpdate = (settings) => {
      const showOldSettings = showSettings.value;
      ampData.value = { ...ampData.value, ...settings };

      toggleFieldsPopup();

      if ((showOldSettings && !showSettings.value) || (!showOldSettings && showSettings.value)) {
        resizeNode();
      }
    };

    const toggleFieldsPopup = () => {
      if (!canEditAmp.value) {
        return;
      }

      showFieldsPopup.value = !showFieldsPopup.value;

      if (showFieldsPopup.value) {
        bringNodeToFront();
      }
    };

    const resizeNode = async () => {
      const nbOfFields = Object.values(ampData.value?.settings || {}).length;

      if (nbOfFields) {
        await nextTick();
        const { width } = nodeSelf.value.size();
        const nodeHeight = nodeRef.value?.$el.offsetHeight;

        if (nodeHeight) {
          nodeSelf.value.resize(width, nodeHeight);
        }
      }
    };

    const getPluginSettings = async (pluginSettings) => {
      if (!ampData.value.pluginId) {
        return;
      }

      loading.value = true;
      const plugin = (await store.value.dispatch(`pluginStore/${GetPluginById}`, ampData.value.pluginId)) || {};
      loading.value = false;

      pluginName.value = plugin.name;
      settings.value =
        plugin.settings?.map((setting) => {
          if (pluginSettings[setting.id] === undefined) {
            pluginSettings[setting.id] = setting.type === 'boolean' ? !!setting.value : setting.value;
          }

          return {
            ...setting,
            label: setting.label || setting.id,
          };
        }) || [];

      if (!rendered.value) {
        rendered.value = true;
      }

      resizeNode();
    };

    return {
      ampData,
      canEditAmp,
      isDone,
      loading,
      maxStorageSize,
      nodeActions,
      nodeId,
      nodeRef,
      nodeType,
      pluginName,
      settings,
      showFieldsPopup,
      showSettings,
      uiData,
      addStorageOption,
      deleteStorage,
      onSettingsUpdate,
      toggleFieldsPopup,
    };
  },
};
